// Copyright 2018 Sisense Inc. All rights reserved.

/**
 * @fileoverview HTML5 based history implementation. Implementation based on
 * Google Closure Library goog.history.Html5History class.
 * @author artem.lit@sisense.com (Artem Lytvynov)
 * @copyright sisense@sisense.com (Sisense Ltd.)
 * @license Sisense License
 */

'use strict';

const utils = require('@sbi/core.utils');
const EventTarget = require('@sbi/core.events').EventTarget;
const HistoryChanged = require('./historychanged').HistoryChanged;

/**
 * HTML5 based history API implementation.
 * @extends {EventTarget}
 * @final
 */
class Html5History extends EventTarget {
    /**
     * @constructor
     */
    constructor() {
        super();

        // Check for Html5History support.
        if (!(window.history && window.history.pushState)) {
            console.error('Your browser do not support HTML5History API'); // eslint-disable-line
        }

        /**
         * @type {Window}
         * @private
         */
        this.window_ = window;

        /**
         * The fragment of the last navigation. Used to eliminate
         * duplicate/redundant NAVIGATE events when a POPSTATE and
         * HASHCHANGE event are triggered for the same navigation (e.g.,
         * back button click).
         * @type {?string}
         * @private
         */
        this.lastFragment_ = null;

        /**
         * Status of when the object is active and dispatching events.
         * @type {boolean}
         * @private
         */
        this.enabled_ = false;

        /**
         * Whether to use the fragment to store the token, defaults to
         * false.
         * @type {boolean}
         * @private
         */
        this.useFragment_ = false;

        /**
         * If useFragment is false the path will be used, the path prefix
         * will be prepended to all tokens. Defaults to '/'.
         * @type {string}
         * @private
         */
        this.pathPrefix_ = '/';


        // Setting up main listeners.
        this.window_.addEventListener(
            'popstate',
            this.onHistoryEvent_.bind(this),
            false
        );
        this.window_.addEventListener(
            'hashchange',
            this.onHistoryEvent_.bind(this),
            false
        );
    }

    /**
     * Gets the URL to set when calling history.pushState
     * @param {string} token The history token.
     * @return {string} The URL.
     * @private
     */
    getUrl_(token) {
        if (this.useFragment_) {
            return `#${token}`;
        } else {
            return this.pathPrefix_ + token + this.window_.location.search;
        }
    }

    /**
     * Gets the current hash fragment, if useFragment_ is enabled.
     * @return {?string} The hash fragment.
     * @private
     */
    getFragment_() {
        if (this.useFragment_) {
            const loc = this.window_.location.href;
            const index = loc.indexOf('#');
            return index < 0 ? '' : loc.substring(index + 1);
        } else {
            return null;
        }
    }

    /**
     * Handles history events dispatched by the browser.
     * @param {Event} e The browser event object.
     * @private
     */
    onHistoryEvent_(e) {
        if (this.enabled_) {
            const fragment = this.getFragment_();
            // Only fire NAVIGATE event if it's POPSTATE or if the fragment
            // has changed without a POPSTATE event. The latter is an
            // indication the browser doesn't support POPSTATE, and the
            // event is a HASHCHANGE instead.
            if (e.type === 'popstate' || fragment !== this.lastFragment_) {
                this.lastFragment_ = fragment;
                this.dispatchEvent(
                    /** @type {Event} */
                    (new HistoryChanged(
                        this.getToken(), true, this
                    ))
                );
            }
        }
    }

    /** @override */
    disposeInternal() {
        super.disposeInternal();
        this.window_.removeEventListener(
            'popstate',
            this.onHistoryEvent_.bind(this),
            false
        );
        this.window_.removeEventListener(
            'hashchange',
            this.onHistoryEvent_.bind(this),
            false
        );
    }

    /**
     * Starts or stops the History.  When enabled, the History object will
     * immediately fire an event for the current location. The caller can
     * set setEnabled.
     * @param {boolean} enable Whether to enable history.
     */
    setEnabled(enable) {
        if (enable === this.enabled_) {
            return;
        }
        this.enabled_ = enable;
        if (enable) {
            this.dispatchEvent(
                /** @type {Event} */
                (new HistoryChanged(
                    this.getToken(), false, this
                ))
            );
        }
    }

    /**
     * Returns the current token.
     * @return {string} The current token.
     */
    getToken() {
        if (this.useFragment_) {
            const fragment = this.getFragment_();
            if (!utils.isString(fragment)) {
                throw TypeError('Fragment should be a string');
            }
            return fragment;
        } else {
            return this.window_.location.pathname.substr(
                this.pathPrefix_.length
            );
        }
    }

    /**
     * Sets the history state.
     * @param {string} token The history state identifier.
     */
    setToken(token) {
        if (token === this.getToken()) {
            return;
        }
        this.window_.history.pushState(
            null,
            this.window_.document.title || '',
            this.getUrl_(token)
        );
        this.dispatchEvent(
            /** @type {Event} */
            (new HistoryChanged(token, false, this))
        );
    }

    /**
     * Replaces the current history state without affecting the rest of the
     * history stack.
     * @param {string} token The history state identifier.
     */
    replaceToken(token) {
        this.window_.history.replaceState(
            null,
            this.window_.document.title || '',
            this.getUrl_(token)
        );
        this.dispatchEvent(
            /** @type {Event} */
            (new HistoryChanged(token, false, this))
        );
    }

    /**
     * Sets whether to use the fragment to store tokens.
     * @param {boolean} useFragment Whether to use the fragment.
     */
    setUseFragment(useFragment) {
        if (this.useFragment_ !== useFragment) {
            if (useFragment) {
                this.window_.addEventListener(
                    'hashchange',
                    this.onHistoryEvent_.bind(this),
                    false
                );
            } else {
                this.window_.removeEventListener(
                    'hashchange',
                    this.onHistoryEvent_.bind(this),
                    false
                );
            }
            this.useFragment_ = useFragment;
        }
    }

    /**
     * Sets the path prefix to use if storing tokens in the path. The path
     * prefix should start and end with slash.
     * @param {string} pathPrefix Sets the path prefix.
     */
    setPathPrefix(pathPrefix) {
        this.pathPrefix_ = pathPrefix;
    }

    /**
     * Gets the path prefix.
     * @return {string} The path prefix.
     */
    getPathPrefix() {
        return this.pathPrefix_;
    }
}

// Exports:
exports.Html5History = Html5History;
